import { extractScriptNodeText } from '@/common/utils/scriptNodeText'

function resolveFormRoot(pageData: unknown): Record<string, unknown> | null {
  if (!pageData || typeof pageData !== 'object') return null
  const root = pageData as { form?: unknown }
  const form = root.form
  if (!form || typeof form !== 'object') return null
  const formRec = form as Record<string, unknown>
  const nested = formRec.form
  if (nested && typeof nested === 'object') {
    const nestedRec = nested as Record<string, unknown>
    const nestedLooksLikeForm =
      nestedRec.panels != null ||
      nestedRec.main != null ||
      nestedRec.onServerEvent != null ||
      nestedRec.header != null ||
      nestedRec.footer != null
    if (nestedLooksLikeForm) return nestedRec
  }
  return formRec
}

function readOnServerEventScript(node: unknown): string {
  const text = extractScriptNodeText(node).trim()
  return text
}

export type OnServerEventScriptEntry = {
  script: string
  panelId?: number
}

function readPanelNumericId(panel: Record<string, unknown>, mapKey?: string): number | undefined {
  const attrs = (panel._attributes ?? panel.attrs) as Record<string, unknown> | undefined
  const raw = panel.panelId ?? attrs?.id ?? panel.id ?? mapKey
  const pid = parseInt(String(raw ?? '').trim(), 10)
  return Number.isFinite(pid) && pid > 0 ? pid : undefined
}

function collectFromPanelsMap(panels: unknown, out: OnServerEventScriptEntry[]): void {
  if (!panels || typeof panels !== 'object') return

  const rec = panels as Record<string, unknown>
  if (rec.panel != null) {
    const list = Array.isArray(rec.panel) ? rec.panel : [rec.panel]
    for (const panel of list) {
      if (!panel || typeof panel !== 'object') continue
      const p = panel as Record<string, unknown>
      const script = readOnServerEventScript(p.onServerEvent)
      if (script) out.push({ script, panelId: readPanelNumericId(p) })
    }
    return
  }

  for (const [mapKey, panel] of Object.entries(rec)) {
    if (!panel || typeof panel !== 'object') continue
    const p = panel as Record<string, unknown>
    const script = readOnServerEventScript(p.onServerEvent)
    if (script) out.push({ script, panelId: readPanelNumericId(p, mapKey) })
  }
}

/**
 * Swing FormBuilder: form onServerEvent + onServerEvent каждой панели.
 * Middle уже отдаёт client-часть (`listeners.add('serverEvent:N', …)`).
 * panelId — чтобы `en.reloadPanel()` без аргумента попадал в панель-источник скрипта.
 */
export function collectOnServerEventScriptEntries(pageData: unknown): OnServerEventScriptEntry[] {
  const form = resolveFormRoot(pageData)
  if (!form) return []

  const entries: OnServerEventScriptEntry[] = []
  const formScript = readOnServerEventScript(form.onServerEvent)
  if (formScript) entries.push({ script: formScript })
  collectFromPanelsMap(form.panels, entries)
  return entries
}

export function collectOnServerEventScripts(pageData: unknown): string[] {
  return collectOnServerEventScriptEntries(pageData).map((entry) => entry.script)
}

export function joinOnServerEventScripts(pageData: unknown): string {
  return collectOnServerEventScripts(pageData).join('\n')
}

/**
 * Резолв numeric panel id по jsName из pageData.form.panels (attrs.jsName / _attributes.jsName).
 */
export function resolvePanelIdByJsName(pageData: unknown, jsName: string): number | null {
  const trimmed = String(jsName ?? '').trim()
  if (!trimmed) return null

  const form = resolveFormRoot(pageData)
  const panels = form?.panels
  if (!panels || typeof panels !== 'object') return null

  const rec = panels as Record<string, unknown>
  const entries: Array<{ id: string; panel: Record<string, unknown> }> = []

  if (rec.panel != null) {
    const list = Array.isArray(rec.panel) ? rec.panel : [rec.panel]
    for (const panel of list) {
      if (!panel || typeof panel !== 'object') continue
      const p = panel as Record<string, unknown>
      const attrs = (p._attributes ?? p.attrs) as Record<string, unknown> | undefined
      const id = String(attrs?.id ?? p.id ?? p.panelId ?? '').trim()
      if (id) entries.push({ id, panel: p })
    }
  } else {
    for (const [id, panel] of Object.entries(rec)) {
      if (!panel || typeof panel !== 'object') continue
      entries.push({ id: String(id), panel: panel as Record<string, unknown> })
    }
  }

  for (const { id, panel } of entries) {
    const attrs = (panel.attrs ?? panel._attributes) as Record<string, unknown> | undefined
    const name = String(attrs?.jsName ?? panel.jsName ?? '').trim()
    if (name === trimmed || id === trimmed) {
      const pid = parseInt(String(panel.panelId ?? id), 10)
      if (!Number.isNaN(pid)) return pid
    }
  }
  return null
}
